
import React, { useEffect, useRef, useState, useCallback } from 'react';

import './CameraComponent.css';

 

/**

* Props

* - tenantId: number (required)

* - eventId?: number (if saving to an event)

* - eventItemId?: number (if saving to a specific event item)

* - onSaved?: (saved:{url:string, fileId?:number, path?:string, reftable:string, tableId:number, parentTableId?:number}) => void

* - onClose: () => void

* - uploadUrl?: string  (default: '/api/upload/camera')

* - onUpload?: (formData: FormData) => Promise<any>  (override upload)

*/

export default function CameraComponent({

  tenantId,

  eventId = null,

  eventItemId = null,

  onSaved,

  onClose,

  uploadUrl = '/api/upload/camera',

  onUpload, // optional custom uploader

}) {

  const videoRef = useRef(null);

  const canvasRef = useRef(null);

  const [isCameraOn, setIsCameraOn] = useState(false);

  const [facingMode, setFacingMode] = useState('environment'); // default rear

  const [stream, setStream] = useState(null);

  const [error, setError] = useState('');

  const [shotDataUrl, setShotDataUrl] = useState(null); // preview after capture

  const [saving, setSaving] = useState(false);

 

  // ---- helpers -------------------------------------------------------------

 

  const stopTracks = useCallback(() => {

    try {

      const s = stream || (videoRef.current && videoRef.current.srcObject);

      if (s) {

        s.getTracks().forEach(t => t.stop());

      }

    } catch (_) {}

  }, [stream]);

 

  const stopCamera = useCallback(() => {

    stopTracks();

    setStream(null);

    setIsCameraOn(false);

  }, [stopTracks]);

 

  const startCamera = useCallback(async (which = facingMode) => {

    setError('');

    try {

      // Prefer exact constraint; if it fails (Safari), fall back to ideal.

      const constraints = {

        video: {

          facingMode: { ideal: which }, // 'environment' or 'user'

          width: { ideal: 1920 },

          height: { ideal: 1080 },

        },

        audio: false,

      };

 

      const s = await navigator.mediaDevices.getUserMedia(constraints);

      if (videoRef.current) {

        videoRef.current.srcObject = s;

        await videoRef.current.play().catch(() => {});

      }

      setStream(s);

      setIsCameraOn(true);

    } catch (e) {

      // If autoplay/permission blocked, surface a button to enable

      console.error('getUserMedia error', e);

      setError(e?.message || 'Unable to start camera');

      setIsCameraOn(false);

    }

  }, [facingMode]);

 

  useEffect(() => {

    // try to start immediately (will no-op if blocked)

    startCamera();

    return () => stopCamera();

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);

 

  const flipCamera = async () => {

    const next = facingMode === 'user' ? 'environment' : 'user';

    setFacingMode(next);

    stopCamera();

    await startCamera(next);

  };

 

  const captureImage = () => {

    if (!videoRef.current || !canvasRef.current) return;

 

    const video = videoRef.current;

    const canvas = canvasRef.current;

    const w = video.videoWidth || 1280;

    const h = video.videoHeight || 720;

 

    canvas.width = w;

    canvas.height = h;

    const ctx = canvas.getContext('2d');

    ctx.drawImage(video, 0, 0, w, h);

 

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

    setShotDataUrl(dataUrl);

  };

 

  const retake = () => setShotDataUrl(null);

 

  const dataUrlToBlob = (dataUrl) => {

    const arr = dataUrl.split(',');

    const mime = arr[0].match(/:(.*?);/)[1] || 'image/jpeg';

    const bstr = atob(arr[1]);

    let n = bstr.length;

    const u8 = new Uint8Array(n);

    while (n--) u8[n] = bstr.charCodeAt(n);

    return new Blob([u8], { type: mime });

  };

 

  const defaultUploader = async (formData) => {

    const r = await fetch(uploadUrl, {

      method: 'POST',

      body: formData,

      credentials: 'include',

    });

    const j = await r.json();

    if (!r.ok) {

      throw new Error(j?.error || j?.message || 'Upload failed');

    }

    return j;

  };

 

  const saveShot = async () => {

    if (!shotDataUrl) return;

    try {

      setSaving(true);

      // Build metadata compatible with your server’s t_file usage:

      // reftable: 't_event' OR 't_event_item'

      // tableid : eventId (if reftable=t_event) or eventItemId (if reftable=t_event_item)

      // parenttableid: eventId when saving an event item

      // (historyRoutes expects these fields to relate images back to events/items)

      const reftable = eventItemId ? 't_event_item' : 't_event';

      const tableId = eventItemId ? Number(eventItemId) : Number(eventId);

      const parentTableId = eventItemId ? Number(eventId) : undefined;

 

      const blob = dataUrlToBlob(shotDataUrl);

      const fname = `camera_${Date.now()}.jpg`;

 

      const fd = new FormData();

      fd.append('file', blob, fname);

      fd.append('tenantId', String(tenantId));

      fd.append('reftable', reftable);

      fd.append('tableid', String(tableId));

      if (parentTableId) fd.append('parenttableid', String(parentTableId));

      fd.append('source', 'camera');

 

      const uploader = onUpload || defaultUploader;

      const result = await uploader(fd);

 

      // Normalize what we return to parent

      const saved = {

        url: result?.url || result?.file?.url || result?.path || result?.file?.path || '',

        fileId: result?.fileId || result?.file?.id,

        path: result?.path || result?.file?.path,

        reftable,

        tableId,

        parentTableId,

      };

 

      onSaved?.(saved);

      onClose?.();

    } catch (e) {

      console.error('Save failed', e);

      alert(e.message || 'Failed to save image');

    } finally {

      setSaving(false);

      stopCamera();

    }

  };

 

  // ---- UI -------------------------------------------------------------------

 

  return (

    <div className="camera-component">

      <div className="camera-stage">

        {!shotDataUrl ? (

          <video ref={videoRef} autoPlay playsInline className="camera-video" />

        ) : (

          <img src={shotDataUrl} className="camera-preview" alt="preview" />

        )}

        <canvas ref={canvasRef} style={{ display: 'none' }} />

      </div>

 

      {/* Top bar */}

      <div className="camera-topbar">

        <button className="btn ghost" onClick={onClose}>Close</button>

        <div className="spacer" />

        <button className="btn ghost" onClick={flipCamera} disabled={!isCameraOn}>

          Flip

        </button>

      </div>

 

      {/* Bottom controls */}

      <div className="camera-controls">

        {!isCameraOn && !shotDataUrl && (

          <button className="btn primary" onClick={() => startCamera()}>

            Enable camera

          </button>

        )}

 

        {isCameraOn && !shotDataUrl && (

          <button className="shutter" aria-label="Capture" onClick={captureImage} />

        )}

 

        {shotDataUrl && (

          <div className="save-row">

            <button className="btn" onClick={retake} disabled={saving}>Retake</button>

            <button className="btn primary" onClick={saveShot} disabled={saving}>

              {saving ? 'Saving…' : 'Save'}

            </button>

          </div>

        )}

 

        {error && <div className="error-msg">{error}</div>}

      </div>

    </div>

  );

}
